// Hover popover that lists the people who reacted with a given emoji.
//
// Figma reference: Ping Design system `3992:74151` — `options-menu`:
//   • Container: #15171C, radius 12, 4px padding, ~253px wide.
//   • Header row: emoji glyph (18/25, 700) + emoji shortcode
//     (`:EYES:`, 12/16.8, 700, #F5F6F8) on the left, `N reacted`
//     (12/16.8, 400, #B5BAC3) right-aligned.
//   • One row per reactor: 28×28 r6 avatar + display name (16/22.4,
//     #F5F6F8) + optional verified badge.
//
// The chat surface only knows reactor *account ids* (the reactions
// projection is name-agnostic — see `sync/useReactions.ts`). The host
// supplies a resolver through `ReactionReactorResolverProvider` so this
// component can turn an account id into a display name / avatar without
// threading a callback through every `MessageRow`.

import React, { createContext, useContext } from 'react';
import { Image, ScrollView, Text, View } from 'react-native';

import { Avatar } from '../components/Avatar/Avatar';
import { VerifiedBadge } from '../components/VerifiedBadge';

/** Resolved identity for a single reactor, used to render a row. */
export interface ReactionReactor {
  readonly accountId: string;
  readonly displayName: string;
  readonly avatarUrl?: string | undefined;
  readonly initials?: string | undefined;
  readonly verified?: boolean | undefined;
  readonly isSelf?: boolean | undefined;
}

export type ReactionReactorResolver = (accountId: string) => ReactionReactor;

const defaultResolver: ReactionReactorResolver = (accountId) => ({
  accountId,
  displayName: accountId.slice(0, 8),
});

const ReactionReactorResolverContext =
  createContext<ReactionReactorResolver>(defaultResolver);

export const ReactionReactorResolverProvider: React.FC<{
  readonly resolve: ReactionReactorResolver;
  readonly children: React.ReactNode;
}> = ({ resolve, children }) => (
  <ReactionReactorResolverContext.Provider value={resolve}>
    {children}
  </ReactionReactorResolverContext.Provider>
);

export function useReactionReactorResolver(): ReactionReactorResolver {
  return useContext(ReactionReactorResolverContext);
}

// Minimal shortcode lookup for the emoji set the composer offers as
// reactions (see `MessageInteractionPanel`). Falls back to no shortcode
// for anything outside the curated set — the header still renders the
// glyph + "N reacted".
const EMOJI_SHORTCODES: Readonly<Record<string, string>> = {
  '👍': 'THUMBSUP',
  '❤️': 'HEART',
  '😂': 'JOY',
  '😮': 'OPEN_MOUTH',
  '😢': 'CRY',
  '🙏': 'PRAY',
  '🎉': 'TADA',
  '🔥': 'FIRE',
  '👏': 'CLAP',
  '✅': 'CHECK',
  '😍': 'HEART_EYES',
  '😡': 'RAGE',
  '🤔': 'THINKING',
  '👀': 'EYES',
  '💯': '100',
  '🙌': 'RAISED_HANDS',
  '😅': 'SWEAT_SMILE',
  '🥳': 'PARTY',
};

function shortcodeFor(emoji: string): string | undefined {
  const code = EMOJI_SHORTCODES[emoji];
  return code !== undefined ? `:${code}:` : undefined;
}

export interface ReactionReactorsPopoverProps {
  readonly emoji: string;
  readonly accountIds: ReadonlyArray<string>;
}

/**
 * Pure-presentational reactor list. Anchoring/visibility is owned by the
 * caller (`ReactionsRow` positions it above the hovered pill).
 */
export const ReactionReactorsPopover: React.FC<ReactionReactorsPopoverProps> = ({
  emoji,
  accountIds,
}) => {
  const resolve = useReactionReactorResolver();
  const shortcode = shortcodeFor(emoji);
  const count = accountIds.length;

  return (
    <View className="w-[253px] rounded-[12px] bg-white dark:bg-dark-surface-panel p-1 shadow-lg border border-light-border-subtle dark:border-dark-border-subtle">
      {/* Header: emoji + shortcode (left), "N reacted" (right) */}
      <View className="flex-row items-center px-2 pb-1 pt-0.5 gap-2.5">
        <View className="flex-row items-center gap-2 flex-1 min-w-0">
          <Text className="text-[18px] leading-[25px]">{emoji}</Text>
          {shortcode !== undefined ? (
            <Text
              numberOfLines={1}
              className="text-[12px] leading-[17px] font-bold text-[#F5F6F8]"
            >
              {shortcode}
            </Text>
          ) : null}
        </View>
        <Text className="text-[12px] leading-[17px] text-[#B5BAC3]">
          {count} reacted
        </Text>
      </View>

      {/* Reactor rows */}
      <ScrollView
        className="max-h-[200px]"
        showsVerticalScrollIndicator={false}
      >
        {accountIds.map((accountId) => {
          const r = resolve(accountId);
          return (
            <View
              key={accountId}
              className="flex-row items-center gap-2 rounded-[12px] px-1 py-1"
            >
              {r.avatarUrl !== undefined ? (
                <Image
                  source={{ uri: r.avatarUrl }}
                  className="h-7 w-7 rounded-[6px]"
                />
              ) : (
                <Avatar
                  size="company"
                  shape="rounded"
                  {...(r.initials !== undefined ? { initials: r.initials } : {})}
                />
              )}
              <Text
                numberOfLines={1}
                className="flex-1 min-w-0 text-[16px] leading-[22px] text-[#F5F6F8]"
              >
                {r.isSelf === true ? 'You' : r.displayName}
              </Text>
              {r.verified === true ? <VerifiedBadge verified size={16} /> : null}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
};

ReactionReactorsPopover.displayName = 'ReactionReactorsPopover';
